import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowRight, ShieldCheck, Wrench, Vote, CalendarDays,
  Receipt, Sparkles, Users, Home as HomeIcon, Gavel, Scale, BellRing, BookOpen,
  Heart, BarChart3, MessageSquare, GraduationCap, Landmark,
} from "lucide-react";
import CommunityIQLogo from "@/components/CommunityIQLogo";

export default function Landing() {
  const [impact, setImpact] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/impact-stats`)
      .then((r) => setImpact(r.data))
      .catch((err) => {
        if (process.env.NODE_ENV !== "production") console.warn("impact stats offline:", err?.message);
      });
  }, []);
  return (
    <div className="min-h-screen paper-texture text-foreground">
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-6 lg:px-10 py-4">
        <div className="flex items-center justify-end gap-3 mb-2">
          <Link
            to="/pricing"
            data-testid="nav-pricing-link"
            className="hidden md:inline-flex text-sm font-medium text-gold hover:text-[hsl(44_95%_78%)] transition-colors"
          >
            Pricing & Add-ons
          </Link>
          <Link
            to="/impact"
            data-testid="nav-impact-link"
            className="hidden md:inline-flex text-sm font-medium text-foreground/85 hover:text-gold transition-colors"
          >
            Impact
          </Link>
          <Link
            to="/login"
            data-testid="nav-login-link"
            className="text-sm font-medium text-foreground/85 hover:text-gold transition-colors"
          >
            Log in
          </Link>
          <Link
            to="/register"
            data-testid="nav-register-link"
            className="text-sm font-medium bg-primary text-primary-foreground px-4 py-2 rounded-full hover:bg-primary/85 transition-colors glow-purple"
          >
            Get started
          </Link>
        </div>
        <div className="flex flex-col items-center justify-center">
          <CommunityIQLogo className="w-full max-w-[900px] h-auto" />
          <p className="text-lg text-gold italic text-center mt-3 max-w-2xl" data-testid="hero-tagline">
            &ldquo;Stronger communities weren&rsquo;t built alone… it takes a village.&rdquo;
          </p>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 lg:pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 lg:pr-6">
          <div className="inline-flex items-center gap-2 bg-accent/60 px-3 py-1.5 rounded-full text-xs font-medium mb-6 border border-gold">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--gold-bright))]" />
            <span className="text-gold">Smarter HOA management. Stronger communities.</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02] mb-6">
            <span className="royal-gradient-text">Smarter HOA management.</span>
            <br />
            <span className="text-gold">Stronger communities.</span>
          </h1>
          <p className="text-lg text-foreground/80 leading-relaxed mb-8 max-w-xl">
            <span className="text-gold font-semibold">CommunityIQ</span> is the warm, dummy-proof platform that helps boards
            run a beautiful community — with self-performing management with AI-assist AI Saint legal templates, gov-doc-aligned escalation workflows,
            a live financial health meter, and <span className="text-gold font-medium">AI Saint</span> to review your
            bylaws for balance and fairness. Every add-on is <span className="text-gold font-medium">free to try for 14 days</span>.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              data-testid="hero-cta-register"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 rounded-full font-medium hover:bg-primary/85 transition-colors glow-purple"
            >
              Create your HOA account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              data-testid="hero-cta-login"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-medium border border-gold text-gold hover:bg-[hsl(var(--gold)/0.08)] transition-colors"
            >
              Board & owner sign in
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-6 text-sm" data-testid="hero-social-proof">
            {[
              {
                v: impact ? String(impact.work_orders_closed_this_month) : "—",
                l: "Work orders closed this month",
                testid: "hero-wo-count",
              },
              {
                v: impact ? String(impact.ai_contracts_generated) : "—",
                l: "AI contracts drafted",
                testid: "hero-contract-count",
              },
              {
                v: impact ? `$${(impact.dollars_managed / 1000).toFixed(1)}k` : "—",
                l: "Under AI project mgmt",
                testid: "hero-dollars",
              },
            ].map((s) => (
              <div key={s.l} data-testid={s.testid}>
                <div className="text-3xl font-semibold text-gold font-mono">{s.v}</div>
                <div className="text-muted-foreground mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Hero image mosaic — trust + beautiful homes */}
        <div className="lg:col-span-6 relative">
          <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[560px]">
            <div className="col-span-4 row-span-4 rounded-3xl overflow-hidden border border-gold royal-ring relative">
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80"
                alt="Classic southern colonial home"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--purple-deep))] via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 bg-card/95 backdrop-blur rounded-xl p-4 border border-gold">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">Board snapshot</span>
                  <span className="text-xs bg-[hsl(var(--gold)/0.2)] text-gold px-2 py-0.5 rounded-full font-medium">Live</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><div className="text-xl font-semibold text-gold font-mono">$8,240</div><div className="text-[10px] text-muted-foreground">Outstanding</div></div>
                  <div><div className="text-xl font-semibold text-gold font-mono">12</div><div className="text-[10px] text-muted-foreground">Requests</div></div>
                  <div><div className="text-xl font-semibold text-gold font-mono">3</div><div className="text-[10px] text-muted-foreground">Meetings</div></div>
                </div>
              </div>
            </div>
            <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden border border-border relative">
              <img
                src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=800&q=80"
                alt="Coastal beach house"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--purple-deep))/0.6] to-transparent" />
            </div>
            <div className="col-span-2 row-span-3 rounded-3xl overflow-hidden border border-border relative">
              <img
                src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=800&q=80"
                alt="Neighbors gathering"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[hsl(var(--purple-deep))/0.75]" />
              <div className="absolute bottom-3 left-3 right-3 text-xs text-gold font-medium flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> Community moments
              </div>
            </div>
            <div className="col-span-4 row-span-2 rounded-3xl overflow-hidden border border-gold relative">
              <img
                src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1200&q=80"
                alt="Southern coastal home with palm trees"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--purple-deep))/0.95] via-[hsl(var(--purple-deep))/0.55] to-transparent" />
              <div className="absolute inset-0 flex items-center px-6">
                <div className="max-w-[55%]">
                  <div className="text-[10px] uppercase tracking-widest text-gold mb-1.5">Homes worth protecting</div>
                  <div className="text-base font-semibold text-foreground leading-tight">Beautifully managed. Mission-driven.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section id="mission" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="hoa-card royal-ring p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[hsl(var(--gold)/0.15)] blur-3xl" />
          <div className="relative max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-primary/25 px-3 py-1.5 rounded-full text-xs font-medium mb-6 border border-gold">
              <Heart className="w-3.5 h-3.5 text-gold" />
              <span className="text-gold">Our mission</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-6">
              <span className="royal-gradient-text">Communities and homes should be safe places.</span>
            </h2>
            <p className="no-gold text-lg text-foreground/85 leading-relaxed mb-4">
              CommunityIQ was created to <span className="text-gold font-semibold">heal communities</span> that have been plagued
              with onerous governing documents and ambiguity, legal costs and legal dependencies for matters that could
              have been resolved with real-time notifications and communications, high property-management costs, contract
              and insurance exposures, non-transparent maintenance decisions and bid awards, vote integrity, and
              bottlenecks.
            </p>
            <p className="no-gold text-lg text-foreground/85 leading-relaxed mb-8">
              We streamline board-of-director management by <span className="text-gold font-semibold">dummy-proofing</span> the
              work — using automation to do the heavy lifting of tracking documents, invoicing, good-faith bidding,
              contract review, notices, and connecting communities and owners to encourage
              <span className="text-gold font-semibold"> common sense and fiscally responsible governance</span> — rather
              than allow the subjective governance and ambiguity that leaves owners and HOAs at odds, reducing morale,
              community, and property values.
            </p>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                ["Less admin cost", "Automation replaces repetitive tasks that were bleeding dues dry."],
                ["Fewer legal fees", "Real-time notices with self-performing management & AI-assist AI Saint drafts resolve issues before lawyers get involved."],
                ["Transparent decisions", "Bids, contracts, votes and maintenance choices — all in the open."],
                ["Restored morale", "Kind reminders, kudos, and clear governance rebuild trust."],
              ].map(([t, d]) => (
                <div key={t} className="border-l-2 border-gold pl-4">
                  <div className="no-gold text-sm font-semibold text-foreground mb-1">{t}</div>
                  <div className="text-xs text-muted-foreground leading-relaxed">{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="hoa-card royal-ring p-6 lg:p-8 grid md:grid-cols-4 gap-6 items-center">
          {[
            { i: ShieldCheck, t: "Bank-grade security", d: "Encrypted at rest & in transit." },
            { i: Scale, t: "Board fiduciary aligned", d: "Advances your nonprofit duty." },
            { i: Heart, t: "Owner-first design", d: "Big buttons, clear language." },
            { i: HomeIcon, t: "Built for real homes", d: "Every community shape & size." },
          ].map((x) => (
            <div key={x.t} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/25 border border-gold flex items-center justify-center flex-shrink-0">
                <x.i className="w-5 h-5 text-gold" strokeWidth={2.2} />
              </div>
              <div>
                <div className="no-gold text-sm font-semibold text-foreground">{x.t}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{x.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-sm uppercase tracking-widest text-gold-soft font-medium mb-3">Everything in one place</p>
          <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            <span className="royal-gradient-text">Two dashboards.</span>
            <br /> One thriving community.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Receipt, title: "Dues & assessments", body: "Track balances per unit with a plain-English ledger. Post charges and payments in seconds." },
            { icon: Wrench, title: "Work orders", body: "Owners submit requests from their phone. Board approves, updates, and closes them out." },
            { icon: ShieldCheck, title: "Violations", body: "Issue warnings, escalate to fines, and keep an audit trail — without the paperwork mess." },
            { icon: CalendarDays, title: "Meetings & agendas", body: "Schedule meetings, share agendas, and store minutes where everyone can find them." },
            { icon: Vote, title: "Polls & votes", body: "Ask the community. Every homeowner gets one clean, verifiable vote." },
            { icon: BellRing, title: "Friendly reminders", body: "Kind nudges to owners — with delivery verification and open tracking. Premium add-on." },
          ].map((f) => (
            <div
              key={f.title}
              className="hoa-card hoa-card-hover p-8 group"
              data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/25 border border-gold flex items-center justify-center mb-5 group-hover:bg-primary/35 transition-colors">
                <f.icon className="w-5 h-5 text-gold" strokeWidth={2.2} />
              </div>
              <h3 className="text-lg font-semibold mb-2 tracking-tight">{f.title}</h3>
              <p className="no-gold text-sm text-foreground/75 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Community photo band */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            "https://images.unsplash.com/photo-1502005097973-6a7082348e28?auto=format&fit=crop&w=800&q=80",
            "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=800&q=80",
            "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=800&q=80",
            "https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?auto=format&fit=crop&w=800&q=80",
          ].map((src) => (
            <div key={src} className="rounded-2xl overflow-hidden border border-border relative aspect-[4/5]">
              <img src={src} alt="Community" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--purple-deep))/0.7] to-transparent" />
            </div>
          ))}
        </div>
      </section>

      {/* AI Saint + Add-ons Section */}
      <section id="ai-saint" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="hoa-card royal-ring p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[hsl(var(--gold)/0.15)] blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-primary/25 px-3 py-1.5 rounded-full text-xs font-medium mb-5 border border-gold">
                <Sparkles className="w-3.5 h-3.5 text-gold" />
                <span className="text-gold">Meet AI Saint · governing-doc review</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight mb-5 leading-tight">
                Bylaws too dense?
                <br />
                <span className="royal-gradient-text">AI Saint reads them for you.</span>
              </h2>
              <p className="no-gold text-lg text-foreground/80 leading-relaxed mb-6">
                Upload past, present, or proposed governing documents. AI Saint returns a plain-English summary,
                a <span className="text-gold font-semibold">balance score</span> showing whether clauses favor the HOA
                or the members, risk flags, and prioritized amendment suggestions worth the vote.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  ["Legal Templates Library", "Demand letters, notices, and liens drafted with AI in seconds.", Gavel],
                  ["Escalation Workflows", "Step-by-step checklists that cite your governing documents.", Scale],
                  ["Friendly Reminders", "Kind nudges to owners with delivery + open verification.", BellRing],
                  ["State-specific Law Library", "Curated statutes for your state, updated as laws change.", BookOpen],
                ].map(([t, d, I]) => (
                  <li key={t} className="flex gap-3 items-start">
                    <I className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-foreground/85"><b className="text-gold">{t}</b> — {d}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/pricing"
                  data-testid="see-pricing-cta"
                  className="inline-flex items-center gap-2 bg-gold text-secondary-foreground px-6 py-3.5 rounded-full font-semibold hover:brightness-110 transition-all glow-gold"
                >
                  See pricing & add-ons <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/register"
                  data-testid="try-free-cta"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold border border-gold text-gold hover:bg-[hsl(var(--gold)/0.08)] transition-colors"
                >
                  Start free · try any add-on
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="royal-bg border border-gold rounded-2xl p-6 royal-ring">
                <div className="flex items-center justify-between mb-5">
                  <div className="text-xs uppercase tracking-widest text-gold">AI Saint · sample review</div>
                  <Sparkles className="w-4 h-4 text-gold" />
                </div>
                <div className="text-xs text-muted-foreground uppercase mb-1">Balance meter</div>
                <div className="relative h-2 rounded-full overflow-hidden bg-muted mb-3">
                  <div className="absolute inset-0 bg-gradient-to-r from-[hsl(355_78%_55%)] via-[hsl(var(--gold))] to-[hsl(200_65%_55%)]" />
                  <div className="absolute top-0 bottom-0" style={{ left: "34%" }}>
                    <div className="w-1 h-4 -mt-1 bg-foreground rounded-full" />
                  </div>
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-5">
                  <span>HOA-favorable</span>
                  <span className="text-gold font-mono">34/100 · HOA-favorable</span>
                  <span>Owner-favorable</span>
                </div>
                <div className="space-y-2">
                  {[
                    { s: "high", t: "Assessment increases lack owner vote threshold" },
                    { s: "medium", t: "Fine schedule unbounded — could invite litigation" },
                    { s: "medium", t: "Architectural review deadlines ambiguous" },
                    { s: "low", t: "Meeting notice period below state statute floor" },
                  ].map((r) => (
                    <div key={r.t} className="flex items-center justify-between text-xs pb-2 border-b border-border">
                      <span className="no-gold text-foreground/80 flex-1">{r.t}</span>
                      <span className={`text-[9px] uppercase tracking-widest font-semibold ml-2 ${r.s === "high" ? "text-destructive" : r.s === "medium" ? "text-gold" : "text-muted-foreground"}`}>{r.s}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 text-[10px] text-muted-foreground italic leading-relaxed">
                  Powered by Claude Sonnet 4.6. Available on Elite plan or as an add-on with a 14-day free trial.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Directors + Owners split */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-8 items-stretch">
        <div className="hoa-card p-10 flex flex-col justify-between overflow-hidden relative min-h-[380px]">
          <img
            src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1200&q=80"
            alt="Board meeting"
            className="absolute inset-0 w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--purple-deep))/0.75] to-[hsl(var(--purple-deep))/0.95]" />
          <div className="relative">
            <span className="text-xs uppercase tracking-widest text-gold-soft font-medium">For directors</span>
            <h3 className="text-3xl font-semibold tracking-tight mt-3 mb-4">Built for directors who care to steward on limited time.</h3>
            <p className="no-gold text-foreground/80 leading-relaxed">
              Every screen shows the one next-best action. Approve a request, post an update, or record a
              payment in a couple of taps — even if you've never touched an HOA tool before.
            </p>
          </div>
          <Link
            to="/register"
            data-testid="directors-cta"
            className="relative inline-flex items-center gap-2 mt-8 text-gold font-medium hover:gap-3 transition-all"
          >
            Try the board dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="hoa-card p-10 flex flex-col justify-between glow-purple min-h-[380px] royal-bg">
          <div>
            <span className="text-xs uppercase tracking-widest text-gold-soft font-medium">For owners</span>
            <h3 className="text-3xl font-semibold tracking-tight mt-3 mb-4">Built for members who want to protect their community and values.</h3>
            <p className="no-gold text-foreground/85 leading-relaxed">
              Check dues, submit a maintenance request, RSVP to the next meeting, and read the news —
              all from a mobile-first dashboard that works like an app.
            </p>
          </div>
          <Link
            to="/login"
            data-testid="owners-cta"
            className="inline-flex items-center gap-2 mt-8 bg-gold text-secondary-foreground px-5 py-3 rounded-full font-semibold w-fit hover:brightness-110 transition-all"
          >
            Sign in as an owner <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Closing */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 text-center">
        <Sparkles className="w-6 h-6 text-gold mx-auto mb-4" />
        <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-3">
          <span className="royal-gradient-text">Beautiful communities</span> deserve beautiful tools.
        </h2>
        <p className="no-gold text-foreground/75 max-w-2xl mx-auto mb-8">
          Give your board an easier week and your community a mission it can be proud of.
        </p>
        <Link
          to="/register"
          data-testid="closing-cta"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full font-medium hover:bg-primary/85 transition-colors glow-purple"
        >
          Start free trial <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} CommunityIQ · Communities beautifully managed.</p>
          <p className="text-xs">Demo: admin@hoa.com / admin123 · board@hoa.com / board123 · owner@hoa.com / owner123</p>
        </div>
      </footer>
    </div>
  );
}
